import { Box, CircularProgress, Link } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { TermTabType } from '../../../common/constants';
import { getGoalPriorityProbability } from '../../../common/helper';
import { useAppDispatch, useAppSelector, useFundingTermScope, useTermFormFields } from '../../../common/hooks';
import { goalCategoryPortfolioMappingsType } from '../../../common/types';
import { BasicButton } from '../../atoms';
import { Icon } from '../../atoms/Icon/Icon';
import { Typography } from '../../atoms/Typography/Typography';
import { ReactComponent as BrokenPageIcon } from '../../../assets/icons/brokenPage.svg';
import { ReactComponent as BrokenPageBgIcon } from '../../../assets/icons/brokenPageBg.svg';
import { GoalCardWrapper } from './GoalCardWrapper';
import { TermEmptyState } from './TermEmptyState';
import { selectEwsResponses } from '../../../features/ews';
import useMarketPortfolioData, { MarketPortfolioTenure } from '../PortfolioComposition/useMarketPortfolioData';
import UpdateFundingGoals from '../../../containers/update-funding-goals/UpdateFundingGoals';
import { getAccountsByDuration, retrySingleEWS, sortGoalsByPriorityOrder } from '../../../utils';

export type FundingNavigatorTermContainerProps = {
    selectedTermIndex: TermTabType;
};

/**
 * Term-specific (Short/Medium/Long) Funding Navigator view. Owns everything scoped to the
 * selected tenure: funding-source mapping, contribution schedules, loading/error/empty states,
 * the underfunded banner + update-goals modal, and rendering the per-goal cards. Subscribes only
 * to the redux slices it needs so a streamed update for one term re-renders just that term.
 */
export const FundingNavigatorTermContainer = ({ selectedTermIndex }: FundingNavigatorTermContainerProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const global = useAppSelector((state) => state.global);
    const { displayedFormFields } = useTermFormFields(selectedTermIndex);

    const adviceClient = useAppSelector((state) => state.adviceClient);
    const goalCategoryPortfolioMappings: goalCategoryPortfolioMappingsType | undefined = useAppSelector(
        (state) => state?.global?.globalConfig?.goalCategoryPortfolioMappings
    );
    const goeConfig = global?.goeConfig || {};
    const { portfolio } = global;
    const supportEmail = global?.globalConfig?.supportEmail || t('APPLICATION_SUPPORT_EMAIL');
    const [isFundingGoalsModalOpen, setIsFundingGoalsModalOpen] = useState(false);

    // AEX-3108: single-portfolio tenants have one `default` term (no Short/Medium/Long tabs).
    const { isMultiPortfolioTenant, durationKey: currentDurationKey, duration: selectedTenure } = useFundingTermScope(
        selectedTermIndex
    );
    const { portfolioCompositionSource } = useMarketPortfolioData(
        isMultiPortfolioTenant ? (selectedTenure as MarketPortfolioTenure) : undefined
    );
    const allMarketPortfolios = portfolioCompositionSource?.length ? portfolioCompositionSource : portfolio;

    // Subscribe only to this term's ews response entry so a streamed update for one term
    // re-renders just this term. Per-goal funding/contribution/runpipe data is read inside
    // each GoalCardWrapper from the ews slice, keeping cards isolated from one another.
    const currentEwsData = useAppSelector((state) => selectEwsResponses(state)?.[currentDurationKey]);

    const isEwsLoading = currentEwsData?.loading === true;
    const isFullEwsError = currentEwsData?.error === true && !currentEwsData?.response?.body;
    const showErrorRetrySection = isFullEwsError;
    const currentRunpipeData = currentEwsData?.response?.body?.runPipeResponses;
    // Goals are displayed in ascending order of `goalPriorityOrder` coming from the EWS
    // response (runPipeResponses), matched to each goal by goalId.
    const orderedGoals = useMemo(
        () => sortGoalsByPriorityOrder(displayedFormFields, currentRunpipeData),
        [displayedFormFields, currentRunpipeData]
    );

    const emptyStateTitleKey = !isMultiPortfolioTenant
        ? 'NO_GOALS_DEFAULT'
        : selectedTermIndex === TermTabType.SHORT
            ? 'NO_GOALS_SHORT'
            : selectedTermIndex === TermTabType.MEDIUM
            ? 'NO_GOALS_MEDIUM'
            : 'NO_GOALS_LONG';

    const noFundingTitleKey = !isMultiPortfolioTenant
        ? 'NO_FUNDING_SOURCES_DEFAULT'
        : selectedTermIndex === TermTabType.SHORT
            ? 'NO_FUNDING_SOURCES_SHORT'
            : selectedTermIndex === TermTabType.MEDIUM
            ? 'NO_FUNDING_SOURCES_MEDIUM'
            : 'NO_FUNDING_SOURCES_LONG';

    const selectedTenureAccountsCount = useMemo(
        () =>
            getAccountsByDuration(
                adviceClient,
                global?.orgInfo?.incomeSourceType?.en,
                global?.orgInfo?.investmentAccountType?.en,
                selectedTenure as any
            ).length,
        [
            adviceClient,
            global?.orgInfo?.incomeSourceType?.en,
            global?.orgInfo?.investmentAccountType?.en,
            selectedTenure
        ]
    );
    const showNoFundingForTenure = useMemo(
        () => displayedFormFields?.length > 0 && selectedTenureAccountsCount === 0,
        [displayedFormFields, selectedTenureAccountsCount]
    );

    const hasUnderfundedGoals = useMemo(() => {
        if (!displayedFormFields?.length || !currentRunpipeData?.length) return false;
        const goalPriorityValue = goeConfig?.goalPriorityProbabilities;
        return displayedFormFields.some((g: any) => {
            const runpipeResponse = currentRunpipeData?.find((run: any) => run.goalId === g.goalId);
            if (runpipeResponse?.noEligibleFundings) return false;

            const analysisReport = runpipeResponse?.response?.body?.analysisReport;
            if (!analysisReport) return false;
            const thresholdValue = Math.round(
                getGoalPriorityProbability(goalPriorityValue, g?.data?.['goalPriority']) * 100
            );
            return (
                Math.round(Number(analysisReport.currentGoalProbability) * 100) < thresholdValue ||
                !analysisReport.isGoalRealistic
            );
        });
    }, [displayedFormFields, currentRunpipeData, goeConfig]);

    const handleRetry = useCallback(() => {
        retrySingleEWS(
            dispatch,
            global?.orgInfo?.incomeSourceType?.en,
            global?.orgInfo?.investmentAccountType?.en,
            adviceClient,
            global?.orgInfo?.orgEmail || '',
            currentDurationKey,
            goalCategoryPortfolioMappings,
            global?.goeConfig?.reallocSchedules
        );
    }, [dispatch, global, adviceClient, goalCategoryPortfolioMappings, currentDurationKey]);
    return (
        <>
            <UpdateFundingGoals
                displayedFormFields={displayedFormFields}
                selectedTermIndex={selectedTermIndex}
                isFundingGoalsModalOpen={isFundingGoalsModalOpen}
                setIsFundingGoalsModalOpen={setIsFundingGoalsModalOpen}
                currentRunpipeData={currentRunpipeData}
            />
            <>
                {hasUnderfundedGoals && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            width: '100%',
                            height: '40px',
                            boxSizing: 'border-box',
                            padding: '8px 16px',
                            mt: '24px',
                            mb: '8px',
                            borderRadius: '6px',
                            overflow: 'hidden',
                            backgroundColor: (theme) => theme.palette.semantic.bg.warning1,
                            border: (theme) => `1px solid ${theme.palette.semantic.bd.warning}`
                        }}
                    >
                        <Box sx={{ display: 'flex', flexShrink: 0, alignItems: 'center' }}>
                            <Icon id="warning-triangle" size={24} />
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flex: 1,
                                minWidth: 0,
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '16px'
                            }}
                        >
                            <Typography
                                as="p"
                                display="inline"
                                variant="labels.large"
                                color="semantic.text.default"
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                <Typography
                                    as="span"
                                    display="inline"
                                    variant="body.largeBold"
                                    color="semantic.text.default"
                                >
                                    {t('ATTENTION_NEEDED')}
                                </Typography>
                                {t('FUNDING_NAVIGATOR_ATTENTION_MESSAGE')}
                            </Typography>
                            <Box
                                component="button"
                                type="button"
                                onClick={() => setIsFundingGoalsModalOpen(true)}
                                sx={{
                                    all: 'unset',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                <Typography
                                    as="span"
                                    color="semantic.int.selected"
                                    variant="labels.largeDemibold"
                                    sx={{ textDecoration: 'underline', whiteSpace: 'nowrap' }}
                                >
                                    {t('UPDATE_FUNDING_GOALS')}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )}
                {isEwsLoading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '167px',
                            width: '100%',
                            mt: '80px',
                            borderRadius: '4px',
                            gap: '16px'
                        }}
                    >
                        <Box sx={{ position: 'relative', width: 96, height: 96 }}>
                            <CircularProgress
                                variant="determinate"
                                value={100}
                                size={96}
                                thickness={4}
                                sx={{ color: (theme) => theme.palette.semantic.bd.subtle }}
                            />
                            <CircularProgress
                                size={96}
                                thickness={4}
                                sx={{
                                    position: 'absolute',
                                    left: 0,
                                    color: (theme) => theme.palette.semantic.icon.brand
                                }}
                            />
                        </Box>
                        <Typography
                            variant="body.large"
                            label={t('PLEASE_WAIT_WHILE_DATA_LOADS')}
                            color="semantic.text.default"
                        />
                    </Box>
                ) : showErrorRetrySection ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '16px',
                            width: '100%',
                            py: '24px'
                        }}
                    >
                        <Box
                            sx={{
                                position: 'relative',
                                width: 56,
                                height: 56,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <BrokenPageBgIcon width={56} height={56} />
                            <BrokenPageIcon width={32} height={32} style={{ position: 'absolute' }} />
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '8px',
                                textAlign: 'center'
                            }}
                        >
                            <Box
                                component="p"
                                sx={{
                                    m: 0,
                                    fontFamily: '"TT Commons Pro"',
                                    fontSize: '18px',
                                    fontWeight: 400,
                                    lineHeight: '25px',
                                    color: (theme) => theme.palette.semantic.text.default
                                }}
                            >
                                {t('FAILED_TO_LOAD_DATA')}
                            </Box>
                            <Box
                                component="p"
                                sx={{
                                    m: 0,
                                    fontFamily: '"TT Commons Pro"',
                                    fontSize: '16px',
                                    fontWeight: 400,
                                    lineHeight: '22px',
                                    color: (theme) => theme.palette.semantic.text.muted
                                }}
                            >
                                <Trans
                                    i18nKey="RETRY_CONTACT_US"
                                    components={{
                                        strong: (
                                            <Link
                                                href={`mailto:${supportEmail}`}
                                                underline="always"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: (theme) => theme.palette.semantic.int.selected,
                                                    cursor: 'pointer'
                                                }}
                                            />
                                        )
                                    }}
                                />
                            </Box>
                        </Box>
                        <BasicButton variant="contained" onClick={() => handleRetry()} sx={{ px: 2, minWidth: 80 }}>
                            {t('TEXT_ERROR_RETRY')}
                        </BasicButton>
                    </Box>
                ) : showNoFundingForTenure ? (
                    <TermEmptyState
                        variant="noFunding"
                        title={t(noFundingTitleKey)}
                        subtitle={t('NO_FUNDING_SOURCES_INFOTEXT')}
                    />
                ) : orderedGoals?.length > 0 ? (
                    <>
                        {orderedGoals?.map((g: any, index) => (
                            <GoalCardWrapper
                                key={`${selectedTermIndex}-${g.goalId || index}`}
                                g={g}
                                selectedTermIndex={selectedTermIndex}
                                goeConfig={goeConfig}
                                portfolioCompositionSource={portfolioCompositionSource}
                                allMarketPortfolios={allMarketPortfolios}
                            />
                        ))}
                    </>
                ) : (
                    <TermEmptyState
                        variant="noGoals"
                        title={t(emptyStateTitleKey)}
                        subtitle={t('FUNDING_NAVIGATOR_NO_GOALS_INFOTEXT')}
                    />
                )}
            </>
        </>
    );
};
